import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    UserPlus,
    Settings2,
    Inbox,
    MessageSquareWarning,
    ShieldCheck,
    Gauge,
    FileSignature,
    Users2,
    Briefcase,
    LineChart,
    Layers,
    Workflow,
    Building2,
    KeyRound,
    Eye,
} from "lucide-react";
import { api } from "../lib/api";
import StatusChip from "../components/StatusChip";

const SPONSOR_JOURNEY = [
    {
        icon: UserPlus,
        title: "Configure partner access",
        body: "Invite brokers, underwriting teams and product leads into a governed referral workspace.",
    },
    {
        icon: Settings2,
        title: "Define referral parameters",
        body: "Set project, product, appetite, authority and evidence parameters for referred cyber cases.",
    },
    {
        icon: Inbox,
        title: "Receive referred cases",
        body: "Automation handles ordinary submissions; edge cases are routed into ReferralCierge.",
    },
    {
        icon: MessageSquareWarning,
        title: "Coordinate clarification",
        body: "Brokers receive structured evidence requests instead of fragmented email chains.",
    },
    {
        icon: ShieldCheck,
        title: "Govern underwriter decisions",
        body: "Underwriters review evidence, authority limits, appetite flags and advisory notes before acting.",
    },
    {
        icon: Gauge,
        title: "Capture feedback",
        body: "Product and appetite teams see recurring friction, coverage requests and referral patterns.",
    },
    {
        icon: FileSignature,
        title: "Prove the action",
        body: "KATLAS signs a receipt showing custody, authority and outcome without exposing raw private data.",
    },
];

const COORDINATION_AXES = [
    { icon: Users2, label: "Distribution partner" },
    { icon: Briefcase, label: "Broker channel" },
    { icon: Layers, label: "Product line" },
    { icon: Workflow, label: "Referral type" },
    { icon: Gauge, label: "Appetite rule" },
    { icon: Building2, label: "Project / pilot" },
    { icon: LineChart, label: "Portfolio segment" },
    { icon: Inbox, label: "Renewal cohort" },
    { icon: Eye, label: "Claims-learning theme" },
];

const STAKEHOLDER_VIEWS = [
    {
        role: "Platform sponsor",
        body: "A governed operating view of referred cases, partner performance, appetite friction and decision evidence.",
    },
    {
        role: "Broker / distribution partner",
        body: "Clear evidence requests, faster clarification, less ambiguity and a visible route to decision.",
    },
    {
        role: "Underwriter",
        body: "A structured referral workbench with authority gates, evidence status, advisory notes and decision receipts.",
    },
    {
        role: "Product / appetite lead",
        body: "A feedback loop showing where appetite, wording or eligibility rules are causing friction.",
    },
    {
        role: "Portfolio / claims reviewer",
        body: "A safe evidence trail linking decisions to later learning without exposing raw client evidence.",
    },
];

export default function Overview() {
    const [cases, setCases] = useState([]);
    const [followCaseId, setFollowCaseId] = useState(null);
    const [latestReceiptHash, setLatestReceiptHash] = useState(null);

    useEffect(() => {
        api.listReferrals()
            .then(async (list) => {
                setCases(list);
                if (list[0]?.id) {
                    setFollowCaseId(list[0].id);
                    const full = await api.getReferral(list[0].id).catch(() => null);
                    const r = full?.receipts?.[full.receipts.length - 1];
                    if (r?.receipt_hash) setLatestReceiptHash(r.receipt_hash);
                }
            })
            .catch(() => setCases([]));
    }, []);

    const totalReceipts = cases.reduce((s, c) => s + (c.receipt_count || 0), 0);
    const totalFlags = cases.reduce((s, c) => s + (c.flag_count || 0), 0);
    const headCase = cases[0];

    return (
        <div className="space-y-20" data-testid="overview-page">
            {/* Hero — sponsor-led, no backend metadata in hero */}
            <section className="grid grid-cols-12 gap-8 lg:gap-12">
                <div className="col-span-12 lg:col-span-9">
                    <p className="label-eyebrow mb-6" data-testid="hero-eyebrow">
                        01 · Sponsor proposition  ·  ReferralCierge · Partner-enabled underwriting governance
                    </p>
                    <h1
                        className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-ink"
                        data-testid="hero-headline"
                    >
                        Give distribution partners a clearer way to handle
                        <span className="block text-forest mt-2">
                            referred cyber cases.
                        </span>
                    </h1>
                    <p
                        className="mt-8 max-w-3xl text-lg text-ink-secondary leading-relaxed"
                        data-testid="hero-subheadline"
                    >
                        ReferralCierge helps platform sponsors coordinate broker clarification, evidence custody,
                        underwriter authority, appetite feedback and decision receipts across referred SME cyber
                        risks — without exposing raw client data or slowing the digital platform.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <Link
                            to="/workbench"
                            className="btn-primary"
                            data-testid="cta-open-sponsor-view"
                        >
                            Open sponsor view
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                        {followCaseId && (
                            <Link
                                to={`/case/${followCaseId}`}
                                className="btn-ghost"
                                data-testid="cta-follow-referred-case"
                            >
                                Follow a referred case
                            </Link>
                        )}
                        <Link
                            to="/diagnostics"
                            className="nav-link"
                            data-testid="hero-katlas-diagnostics-link"
                        >
                            KATLAS diagnostics →
                        </Link>
                    </div>
                </div>
            </section>

            {/* In plain language — the concrete scenario */}
            <section data-testid="plain-language-scenario">
                <div className="panel">
                    <div className="grid grid-cols-12">
                        <div className="col-span-12 md:col-span-4 px-6 md:px-8 py-6 md:py-8 border-b md:border-b-0 md:border-r border-parchment-rule bg-[#F2EFE9]">
                            <p className="label-eyebrow mb-3">02 · The case</p>
                            <p
                                className="font-serif text-2xl text-ink leading-snug"
                                data-testid="plain-language-the-case"
                            >
                                A broker submits a cyber risk that falls outside automated appetite.
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-8 px-6 md:px-8 py-6 md:py-8">
                            <p className="label-eyebrow mb-3">03 · What ReferralCierge does</p>
                            <p
                                className="font-serif text-2xl text-ink leading-snug"
                                data-testid="plain-language-what-rc-does"
                            >
                                ReferralCierge shows the sponsor, broker and underwriter what evidence is needed,
                                who may decide, and what receipt proves the outcome.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sponsor journey */}
            <section data-testid="sponsor-journey-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <p className="label-eyebrow mb-2">04 · Sponsor journey</p>
                        <h2 className="font-serif text-3xl sm:text-4xl text-ink">
                            How the platform sponsor uses ReferralCierge
                        </h2>
                    </div>
                    <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                        7 governed steps
                    </span>
                </div>
                <div className="panel">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                        {SPONSOR_JOURNEY.map((step, i) => {
                            const Icon = step.icon;
                            const total = SPONSOR_JOURNEY.length;
                            const lgCols = 4;
                            const isLastRow = Math.floor(i / lgCols) === Math.floor((total - 1) / lgCols);
                            const isLastCol = (i + 1) % lgCols === 0;
                            return (
                                <div
                                    key={step.title}
                                    className={
                                        "p-5 md:p-6 border-parchment-rule " +
                                        (isLastRow ? "" : "border-b ") +
                                        (isLastCol ? "" : "lg:border-r ")
                                    }
                                    data-testid={`sponsor-journey-step-${i + 1}`}
                                >
                                    <div className="flex items-baseline justify-between mb-3">
                                        <Icon size={18} strokeWidth={1.5} className="text-forest" />
                                        <span className="font-mono text-[11px] text-ink-muted">
                                            Step {i + 1}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-xl text-ink mb-2 leading-tight">
                                        {step.title}
                                    </h3>
                                    <p className="text-sm text-ink-secondary leading-relaxed">
                                        {step.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Ecosystem coordination */}
            <section data-testid="ecosystem-coordination-section">
                <div className="grid grid-cols-12 gap-8 lg:gap-12">
                    <div className="col-span-12 lg:col-span-4">
                        <p className="label-eyebrow mb-3">05 · Stakeholder coordination</p>
                        <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
                            Coordinate by partner, project, case or portfolio
                        </h2>
                        <p className="mt-5 text-base text-ink-secondary leading-relaxed">
                            The sponsor can organise referred work along the axes that matter to the book —
                            distribution, product, appetite, project and learning — without forcing distribution
                            partners through a single workflow.
                        </p>
                        <p className="mt-4 text-sm text-ink-muted font-mono uppercase tracking-[0.18em]">
                            Explainer only · admin console out of scope for this demo
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-8">
                        <div className="panel">
                            <div className="grid grid-cols-2 sm:grid-cols-3">
                                {COORDINATION_AXES.map((axis, i) => {
                                    const Icon = axis.icon;
                                    const cols = 3;
                                    const total = COORDINATION_AXES.length;
                                    const isLastRow = Math.floor(i / cols) === Math.floor((total - 1) / cols);
                                    const isLastCol = (i + 1) % cols === 0;
                                    return (
                                        <div
                                            key={axis.label}
                                            className={
                                                "p-5 border-parchment-rule " +
                                                (isLastRow ? "" : "border-b ") +
                                                (isLastCol ? "" : "sm:border-r ")
                                            }
                                            data-testid={`coordination-axis-${axis.label.toLowerCase().replace(/\s+/g, "-").replace(/[/]/g, "")}`}
                                        >
                                            <Icon size={16} strokeWidth={1.5} className="text-forest mb-3" />
                                            <div className="font-serif text-lg text-ink leading-tight">
                                                {axis.label}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What each stakeholder gets */}
            <section data-testid="stakeholder-views-section">
                <p className="label-eyebrow mb-3">05 · Stakeholder coordination — what each role gets</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-8 max-w-3xl leading-tight">
                    What each stakeholder gets
                </h2>
                <div className="panel divide-y divide-parchment-rule">
                    {STAKEHOLDER_VIEWS.map((s) => (
                        <div
                            key={s.role}
                            className="grid grid-cols-12 gap-4 px-5 md:px-6 py-5"
                            data-testid={`stakeholder-${s.role.toLowerCase().replace(/\s+/g, "-").replace(/[/]/g, "")}`}
                        >
                            <div className="col-span-12 md:col-span-4 lg:col-span-3 label-eyebrow self-start pt-1">
                                {s.role}
                            </div>
                            <div className="col-span-12 md:col-span-8 lg:col-span-9 text-base text-ink leading-relaxed">
                                {s.body}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why KATLAS sits underneath */}
            <section data-testid="katlas-underneath-section">
                <div className="grid grid-cols-12 gap-8 lg:gap-12">
                    <div className="col-span-12 lg:col-span-7">
                        <p className="label-eyebrow mb-3">06 · Why KATLAS sits underneath</p>
                        <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
                            Governance for the actions, not a replacement for the platform.
                        </h2>
                        <p className="mt-6 text-base text-ink-secondary leading-relaxed max-w-2xl">
                            KATLAS does not replace the underwriting platform. It governs the actions around the
                            platform: who requested evidence, who had authority, what was shared, what was withheld,
                            what decision was made and what receipt proves it.
                        </p>
                        <p className="mt-6 font-serif text-2xl text-ink leading-snug max-w-2xl">
                            Custody controls the evidence.
                            <br />
                            Authority governs the action.
                            <br />
                            Receipts prove what happened.
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-5">
                        <div className="panel">
                            <CARRow
                                icon={KeyRound}
                                label="Custody"
                                body="Brokers and clients retain raw evidence. References and counts only."
                            />
                            <CARRow
                                icon={ShieldCheck}
                                label="Authority"
                                body="Named human role authorises every governed action. Gates are visible."
                            />
                            <CARRow
                                icon={FileSignature}
                                label="Receipts"
                                body="Signed envelope kept server-side. Public receipt shows metadata only."
                                last
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Stage 07 — Workbench & receipt journey handoff */}
            {cases.length > 0 && (
                <section data-testid="workbench-journey-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <p className="label-eyebrow mb-2">07 · Workbench & receipt journey</p>
                            <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
                                Step into the workbench
                            </h2>
                            <p className="mt-3 text-base text-ink-secondary max-w-2xl">
                                Follow the referred case end-to-end: review the broker submission and automated
                                flags, request clarification, watch evidence custody, decide within authority and
                                read the signed KATLAS receipt.
                            </p>
                        </div>
                        <span className="hidden md:inline font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                            {cases.length} referred case · {totalFlags} flag · {totalReceipts} receipt
                        </span>
                    </div>

                    <div className="grid grid-cols-12 gap-6 lg:gap-8">
                        {/* Case entry card */}
                        {headCase && (
                            <Link
                                to={`/case/${headCase.id}`}
                                className="col-span-12 lg:col-span-7 panel p-6 md:p-7 hover:bg-[#F2EFE9] transition-colors"
                                data-testid={`workbench-entry-case-${headCase.id}`}
                            >
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <div className="data-cell text-xs text-ink-muted mb-2">
                                            {headCase.submission_ref}
                                        </div>
                                        <div className="font-serif text-2xl text-ink leading-tight">
                                            {headCase.insured_label}
                                        </div>
                                        <div className="mt-1 text-sm text-ink-secondary">
                                            {headCase.broker_partner}  ·  {headCase.sector}
                                        </div>
                                    </div>
                                    <StatusChip status={headCase.status} />
                                </div>
                                <div className="grid grid-cols-3 gap-4 mt-6 border-t border-parchment-rule pt-5">
                                    <Stat label="Limit requested" value={`$${(headCase.policy_limit_requested_usd / 1_000_000).toFixed(1)}m`} />
                                    <Stat label="Referral flags" value={headCase.flag_count} />
                                    <Stat label="Receipts recorded" value={headCase.receipt_count} />
                                </div>
                                <p className="mt-6 text-sm text-ink-muted font-mono uppercase tracking-[0.18em]">
                                    Follow this case →
                                </p>
                            </Link>
                        )}

                        {/* Receipt envelope hint */}
                        <div
                            className="col-span-12 lg:col-span-5 receipt-paper border border-parchment-rule p-6 md:p-7 flex flex-col"
                            data-testid="workbench-entry-receipt-hint"
                        >
                            <div className="flex items-baseline justify-between mb-4">
                                <span className="label-eyebrow">Latest KATLAS receipt</span>
                                <StatusChip
                                    status={latestReceiptHash ? "unsigned" : "unverified"}
                                    label={latestReceiptHash ? "Recorded · Stage 1 unsigned" : "No receipt yet"}
                                />
                            </div>
                            {latestReceiptHash ? (
                                <>
                                    <div className="mb-2 label-eyebrow">Receipt hash</div>
                                    <div className="data-cell text-xs break-all mb-5" data-testid="workbench-latest-receipt-hash">
                                        {latestReceiptHash}
                                    </div>
                                    <p className="text-sm text-ink leading-relaxed mb-6">
                                        The public receipt shows only governance metadata — hash, signer reference,
                                        timestamp, outcome, shared evidence refs, withheld-evidence count and CAR
                                        summary. The full signed envelope stays server-side.
                                    </p>
                                    <Link
                                        to={`/r/${encodeURIComponent(latestReceiptHash)}`}
                                        className="btn-primary self-start mt-auto"
                                        data-testid="workbench-view-latest-receipt"
                                    >
                                        View latest receipt
                                        <ArrowRight size={14} strokeWidth={1.5} />
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-ink leading-relaxed mb-6">
                                        No governed action has been recorded against this case yet. Open the
                                        workbench, request clarification or submit evidence — each governed action
                                        produces a KATLAS receipt with a public-safe view.
                                    </p>
                                    <Link
                                        to={`/case/${headCase?.id || ""}`}
                                        className="btn-primary self-start mt-auto"
                                        data-testid="workbench-start-flow"
                                    >
                                        Start the flow
                                        <ArrowRight size={14} strokeWidth={1.5} />
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Secondary nav row */}
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link to="/workbench" className="btn-primary" data-testid="workbench-cta-open">
                            Open workbench
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                        <Link to="/portfolio" className="btn-ghost" data-testid="workbench-cta-portfolio">
                            Portfolio view
                        </Link>
                        <Link to="/diagnostics" className="nav-link" data-testid="workbench-cta-diagnostics">
                            KATLAS diagnostics →
                        </Link>
                    </div>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="label-eyebrow mb-1">{label}</div>
            <div className="font-serif text-2xl text-ink leading-none">{value}</div>
        </div>
    );
}

function CARRow({ icon: Icon, label, body, last }) {
    return (
        <div
            className={
                "px-5 md:px-6 py-5 flex items-start gap-4 " +
                (last ? "" : "border-b border-parchment-rule")
            }
        >
            <Icon size={16} strokeWidth={1.5} className="text-forest mt-1 shrink-0" />
            <div>
                <div className="label-eyebrow mb-1">{label}</div>
                <p className="text-sm text-ink leading-relaxed">{body}</p>
            </div>
        </div>
    );
}
